import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

const REMEMBER_KEY = "eva_remember_me";
const SESSION_CHECK_INTERVAL_MS = 10000; // 10s

const LOGVI_API_BASE = "https://api.logvi-havox.com";
const GOOGLEMARINE_API_BASE = "https://api.eva-googlemarine.com";

function getApiBase(): string {
  if (typeof window === "undefined") {
    return LOGVI_API_BASE;
  }

  const hostname = window.location.hostname.toLowerCase();

  const isLocalhost =
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

  if (isLocalhost) {
    return LOGVI_API_BASE;
  }

  if (hostname.includes("logvi-havox")) {
    return LOGVI_API_BASE;
  }

  return GOOGLEMARINE_API_BASE;
}

export type SubitemMenu = {
  label: string;
  codigo: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string | null;
  children?: SubitemMenu[];
};

export type ModuloDepartamento = {
  label: string;
  codigo: string;
  themeColor?: string | null;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  subitems?: SubitemMenu[];
};

export type DepartamentoAcessivel = {
  id: number | string;
  nome: string;
  slug: string;
  routeKey?: string | null;
  themeColor?: string | null;
  iconKey?: string | null;
  modulos?: ModuloDepartamento[];
};

export type EmpresaSessao = {
  id?: number | string | null;
  razao_social?: string | null;
  cnpj?: string | null;
};

export type Sessao = {
  id: number;
  nome: string;
  email?: string;
  picture?: string | number | null;
  departamento?: string | null;
  funcao?: string | null;
  ativo?: boolean;
  empresa_razao_social?: string | null;
  empresa_cnpj?: string | null;

  empresa?: EmpresaSessao | null;

  empresa_id?: string | number | null;
  roles?: string[];
  solicitante_id?: string | number | null;
  conta_id?: string | number | null;
  uid?: string | number | null;

  scopes?: string[];
  ttl?: number | null;
  ts?: number | null;
  token_type?: string | null;
  device_id?: string | null;
  is_shared_device?: boolean;

  departamentos_acessiveis?: DepartamentoAcessivel[];
};

type AuthContextValue = {
  loading: boolean;
  sessao: Sessao | null;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  apiBase: string;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function mapSubitemRecursive(sub: any): SubitemMenu {
  return {
    label: String(sub?.label || ""),
    codigo: String(sub?.codigo || ""),
    devOnly: sub?.devOnly === true || sub?.dev_only === true,
    developed: sub?.developed === true,
    migrated: sub?.migrated === true,
    markOrange: sub?.markOrange === true || sub?.mark_orange === true,
    markBlue: sub?.markBlue === true || sub?.mark_blue === true,
    themeColor:
      sub?.themeColor !== undefined && sub?.themeColor !== null
        ? String(sub.themeColor)
        : sub?.theme_color !== undefined && sub?.theme_color !== null
        ? String(sub.theme_color)
        : null,
    children: Array.isArray(sub?.children)
      ? sub.children.map((child: any) => mapSubitemRecursive(child))
      : [],
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hadValidSession, setHadValidSession] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setTheme, setSystemTitle } = useTheme();

  const apiBase = useMemo(() => getApiBase(), []);

  const rememberMe = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(REMEMBER_KEY) || "false") === true;
    } catch {
      return false;
    }
  }, []);

  const clearRemember = useCallback(() => {
    if (!rememberMe) {
      try {
        localStorage.removeItem(REMEMBER_KEY);
      } catch {
        // ignora
      }
    }
  }, [rememberMe]);

  const applyConfigFromApi = useCallback(
    (config: any) => {
      if (!config) return;

      if (config.theme) {
        try {
          setTheme(config.theme);
        } catch {
          // ignora valor inválido
        }
      }

      const stRaw = config.systemTitle;
      let st: string | null = null;

      if (Array.isArray(stRaw)) {
        st = stRaw.join(" • ");
      } else if (typeof stRaw === "string") {
        st = stRaw;
      }

      if (st) {
        setSystemTitle(st);
      }
    },
    [setTheme, setSystemTitle]
  );

  const refresh = useCallback(
    async (fromInterval: boolean = false) => {
      if (!fromInterval) {
        setLoading(true);
        setError(null);
      }

      try {
        const r = await fetch(`${apiBase}/auth/me`, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!r.ok) {
          clearRemember();
          setSessao(null);

          if (hadValidSession) {
            const path = location.pathname;
            const isOnLogin = path === "/entrar" || path === "/login";
            const isOnSessaoEncerrada = path === "/sessao-encerrada";

            if (!isOnLogin && !isOnSessaoEncerrada) {
              navigate("/sessao-encerrada", { replace: true });
            }
          }

          return;
        }

        const data = await r.json().catch(() => null);

        if (data?.ok && data?.sessao?.nome) {
          const raw = data.sessao;

          const roles: string[] = Array.isArray(raw.roles)
            ? raw.roles.map((x: any) => String(x))
            : [];

          const scopes: string[] = Array.isArray(raw.scopes)
            ? raw.scopes.map((x: any) => String(x))
            : [];

          const departamentosAcessiveis: DepartamentoAcessivel[] =
            Array.isArray(raw.departamentos_acessiveis)
              ? raw.departamentos_acessiveis.map((dep: any) => ({
                  id: dep?.id,
                  nome: String(dep?.nome || ""),
                  slug: String(dep?.slug || ""),
                  routeKey:
                    dep?.routeKey !== undefined && dep?.routeKey !== null
                      ? String(dep.routeKey)
                      : dep?.route_key !== undefined && dep?.route_key !== null
                      ? String(dep.route_key)
                      : null,
                  themeColor:
                    dep?.themeColor !== undefined && dep?.themeColor !== null
                      ? String(dep.themeColor)
                      : dep?.theme_color !== undefined &&
                        dep?.theme_color !== null
                      ? String(dep.theme_color)
                      : null,
                  iconKey:
                    dep?.iconKey !== undefined && dep?.iconKey !== null
                      ? String(dep.iconKey)
                      : dep?.icon_key !== undefined && dep?.icon_key !== null
                      ? String(dep.icon_key)
                      : null,
                  modulos: Array.isArray(dep?.modulos)
                    ? dep.modulos.map((mod: any) => ({
                        label: String(mod?.label || ""),
                        codigo: String(mod?.codigo || ""),
                        themeColor:
                          mod?.themeColor !== undefined &&
                          mod?.themeColor !== null
                            ? String(mod.themeColor)
                            : mod?.theme_color !== undefined &&
                              mod?.theme_color !== null
                            ? String(mod.theme_color)
                            : null,
                        devOnly:
                          mod?.devOnly === true || mod?.dev_only === true,
                        developed: mod?.developed === true,
                        migrated: mod?.migrated === true,
                        markOrange:
                          mod?.markOrange === true || mod?.mark_orange === true,
                        markBlue:
                          mod?.markBlue === true || mod?.mark_blue === true,
                        subitems: Array.isArray(mod?.subitems)
                          ? mod.subitems.map((sub: any) =>
                              mapSubitemRecursive(sub)
                            )
                          : [],
                      }))
                    : [],
                }))
              : [];

          const s: Sessao = {
            id: Number(raw.id ?? raw.uid ?? 0),
            uid: raw.uid ?? null,
            nome: String(raw.nome || ""),
            email: raw.email ?? undefined,
            picture: raw.picture ?? null,

            departamento:
              typeof raw.departamento === "string"
                ? String(raw.departamento)
                : null,

            funcao: typeof raw.funcao === "string" ? String(raw.funcao) : null,

            ativo:
              raw.ativo !== undefined && raw.ativo !== null
                ? raw.ativo === true
                : undefined,

            empresa_razao_social: raw.empresa_razao_social ?? null,
            empresa_cnpj: raw.empresa_cnpj ?? null,

            empresa:
              data?.empresa && typeof data.empresa === "object"
                ? {
                    id:
                      data.empresa.id !== undefined && data.empresa.id !== null
                        ? data.empresa.id
                        : null,
                    razao_social:
                      data.empresa.razao_social !== undefined
                        ? data.empresa.razao_social
                        : raw.empresa_razao_social ?? null,
                    cnpj:
                      data.empresa.cnpj !== undefined
                        ? data.empresa.cnpj
                        : raw.empresa_cnpj ?? null,
                  }
                : {
                    id:
                      raw.empresa_id !== undefined && raw.empresa_id !== null
                        ? raw.empresa_id
                        : null,
                    razao_social: raw.empresa_razao_social ?? null,
                    cnpj: raw.empresa_cnpj ?? null,
                  },

            empresa_id:
              raw.empresa_id !== undefined && raw.empresa_id !== null
                ? raw.empresa_id
                : null,

            roles,
            scopes,

            solicitante_id:
              raw.solicitante_id !== undefined && raw.solicitante_id !== null
                ? raw.solicitante_id
                : null,

            conta_id:
              raw.conta_id !== undefined && raw.conta_id !== null
                ? raw.conta_id
                : null,

            ttl:
              raw.ttl !== undefined && raw.ttl !== null
                ? Number(raw.ttl)
                : null,

            ts: raw.ts !== undefined && raw.ts !== null ? Number(raw.ts) : null,

            token_type:
              raw.token_type !== undefined && raw.token_type !== null
                ? String(raw.token_type)
                : null,

            device_id:
              raw.device_id !== undefined && raw.device_id !== null
                ? String(raw.device_id)
                : null,

            is_shared_device: raw.is_shared_device === true,

            departamentos_acessiveis: departamentosAcessiveis,
          };

          setSessao(s);
          setHadValidSession(true);
          applyConfigFromApi(data.config || null);
        } else {
          clearRemember();
          setSessao(null);
        }
      } catch (e: any) {
        if (!fromInterval) {
          setError(e?.message || "Erro ao carregar sessão");
        }
        setSessao(null);
      } finally {
        if (!fromInterval) {
          setLoading(false);
        }
      }
    },
    [
      apiBase,
      applyConfigFromApi,
      clearRemember,
      hadValidSession,
      location.pathname,
      navigate,
    ]
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }).catch(() => null);
    } finally {
      try {
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem("access_token");
      } catch {
        // ignora
      }

      setSessao(null);
      setHadValidSession(false);
      window.location.assign("/entrar");
    }
  }, [apiBase]);

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => {
      refresh(true);
    }, SESSION_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [refresh]);

  const value: AuthContextValue = {
    loading,
    sessao,
    error,
    refresh: () => refresh(false),
    logout,
    apiBase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  }
  return ctx;
}
